import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";

export const useAiCredits = (enabled = true) => useQuery({ queryKey: ["ai-credits"], enabled, queryFn: async () => (await api.get("/ai/credits")).data });

const NoCredits = ({ msg, user, t, dark }) => (
  <div className={`mt-3 rounded-xl p-3 text-sm ${dark ? "bg-white/10 text-white" : "bg-[#FFF0EE] text-[#9A3412]"}`} data-testid="ai-no-credits">
    <p>{msg}</p>
    <Link to={user ? "/account?tab=payments" : "/accedi"} className="btn-coral mt-2 !py-1.5 text-xs" data-testid="ai-buy-pack-link">{user ? t("p5.ai.buy") : t("p5.ai.login")}</Link>
  </div>
);

export const AiSearch = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: credits } = useAiCredits(!!user && user.role !== "admin" && user.role !== "moderator");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [blocked, setBlocked] = useState("");
  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setBlocked("");
    try { setRes((await api.post("/ai/search", { query: q })).data); } catch (err) { if (err?.response?.status === 402) { setBlocked(errMsg(err)); setRes(null); } else toast.error(errMsg(err)); }
    if (user) qc.invalidateQueries({ queryKey: ["ai-credits"] });
    setBusy(false);
  };
  return (
    <div data-testid="ai-search">
      <form onSubmit={run} className="mt-5 flex gap-2">
        <input className="field !bg-white/95 !text-[#1E1B2E]" value={q} onChange={(e) => setQ(e.target.value)} minLength={3} maxLength={300} placeholder={t("p4.ai.placeholder")} data-testid="ai-search-input" />
        <button disabled={busy} className="btn-coral shrink-0" data-testid="ai-search-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}</button>
      </form>
      <p className="mt-2 text-xs text-white/60">{t("p4.ai.disclaimer")}</p>
      <p className="mt-1 text-xs font-semibold text-amber-200" data-testid="ai-credits-line">{!user ? (blocked ? "" : t("p5.ai.guestTrial")) : credits ? `${t("p5.ai.left")}: ${credits.total_left}${credits.pro_active ? ` (${credits.pro_free_left} ${t("p5.ai.freePro")})` : ""}` : ""}</p>
      {blocked && <NoCredits msg={blocked} user={user} t={t} dark />}
      {res && (
        <div className="mt-4 rounded-2xl bg-white p-4 text-[#1E1B2E]" data-testid="ai-search-results">
          <p className="text-xs text-[#64748B]">{t("p4.ai.interpreted")}: {Object.entries(res.interpreted_filters).map(([k, v]) => `${k}=${v}`).join(", ") || "—"}</p>
          <p className="mt-1 text-sm font-semibold">{res.total} {t("search.results")} <Link to={`/cerca?${new URLSearchParams(res.interpreted_filters)}`} className="ml-2 text-[#3B1272] underline">{t("home.seeAll")}</Link></p>
          {!res.items.length ? <p className="mt-2 text-sm text-[#64748B]" data-testid="ai-search-empty">{t("p4.ai.none")}</p>
            : <div className="mt-3 grid grid-cols-2 gap-3">{res.items.slice(0, 4).map((l) => <ListingCard key={l.listing_id} l={l} />)}</div>}
        </div>
      )}
    </div>
  );
};

export const AiDraft = ({ onApply }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState(null);
  const [blocked, setBlocked] = useState("");
  const run = async () => {
    setBusy(true); setBlocked("");
    try { setD((await api.post("/ai/draft", { notes })).data); } catch (e) { e?.response?.status === 402 ? setBlocked(errMsg(e)) : toast.error(errMsg(e)); }
    qc.invalidateQueries({ queryKey: ["ai-credits"] });
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-dashed border-[#C9B8EC] bg-[#FBF9FF] p-4" data-testid="ai-draft">
      <p className="flex items-center gap-2 text-sm font-semibold text-[#3B1272]"><Sparkles className="h-4 w-4" />{t("p4.ai.draftTitle")}</p>
      <textarea className="field mt-2" maxLength={1500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("p4.ai.draftPh")} data-testid="ai-draft-notes" />
      <button type="button" onClick={run} disabled={busy || notes.length < 5} className="btn-ghost mt-2 !py-2 text-sm" data-testid="ai-draft-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("p4.ai.draftBtn")}</button>
      {blocked && <NoCredits msg={blocked} user={user} t={t} />}
      {d && (
        <div className="mt-3 space-y-1 rounded-xl bg-white p-3 text-sm" data-testid="ai-draft-result">
          <p className="text-[11px] font-bold uppercase text-[#E0533F]">{t("p4.ai.suggestion")}</p>
          <p className="font-semibold">{d.title}</p><p className="text-[#4B4763]">{d.description}</p>
          {d.missing_info.length > 0 && <p className="text-xs text-amber-700">{t("p4.ai.missing")}: {d.missing_info.join(" · ")}</p>}
          <button type="button" onClick={() => { onApply(d); setD(null); }} className="btn-violet mt-2 !py-2 text-sm" data-testid="ai-draft-apply">{t("p4.ai.apply")}</button>
        </div>
      )}
    </div>
  );
};
